'use client'

import { useEffect, useId, useRef, useState } from 'react'
import Link from 'next/link'
import { usePathname } from 'next/navigation'
import { cn } from '@/lib/utils'
import { menuContainsPath, type NavLink, type NavMenuItem } from '@/lib/nav'

/**
 * A desktop header dropdown, built as a disclosure (a button that shows and
 * hides a list of links) rather than an ARIA `menu`. Site navigation is a list
 * of links; `role="menu"` would promise application-style arrow-key behaviour
 * and switch screen readers into a mode where ordinary Tab navigation fails.
 *
 * The header stays mounted across client navigations, so the panel closes on a
 * pathname change as well as on Escape, an outside press and focus leaving it —
 * which is also why a native <details> would not do: it stays open after the
 * link inside it is followed.
 */
export default function NavMenu({ menu }: { menu: NavMenuItem }) {
  const [open, setOpen] = useState(false)
  const pathname = usePathname()
  const panelId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const buttonRef = useRef<HTMLButtonElement>(null)
  const active = menuContainsPath(menu, pathname)

  useEffect(() => setOpen(false), [pathname])

  useEffect(() => {
    if (!open) return
    function onPointerDown(e: PointerEvent) {
      if (!rootRef.current?.contains(e.target as Node)) setOpen(false)
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false)
        buttonRef.current?.focus()
      }
    }
    document.addEventListener('pointerdown', onPointerDown)
    document.addEventListener('keydown', onKeyDown)
    return () => {
      document.removeEventListener('pointerdown', onPointerDown)
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const wide = menu.groups.length > 1

  return (
    <div
      ref={rootRef}
      className="relative"
      onBlur={(e) => {
        if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setOpen(false)
      }}
    >
      <button
        ref={buttonRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        onClick={() => setOpen((prev) => !prev)}
        className={cn(
          'flex items-center gap-1.5 text-sm font-medium transition-colors duration-200 hover:text-fg',
          active || open ? 'text-fg' : 'text-fg-secondary'
        )}
      >
        {menu.label}
        <svg
          width="10"
          height="10"
          viewBox="0 0 10 10"
          fill="none"
          aria-hidden="true"
          className={cn(
            'h-2.5 w-2.5 shrink-0 transition-transform duration-200',
            open && 'rotate-180'
          )}
        >
          <path
            d="M2 3.5L5 6.5L8 3.5"
            stroke="currentColor"
            strokeWidth="1.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      </button>

      <div
        id={panelId}
        hidden={!open}
        className={cn(
          'absolute left-1/2 top-full z-50 mt-4 -translate-x-1/2 rounded-lg border border-line-strong bg-surface-raised p-6',
          !wide ? 'w-80' : menu.groups.length > 3 ? 'w-[52rem]' : 'w-[40rem]'
        )}
      >
        <div
          className={cn(
            'grid gap-6',
            wide && (menu.groups.length > 3 ? 'grid-cols-4' : 'grid-cols-3')
          )}
        >
          {menu.groups.map((group, i) => (
            <div key={group.heading?.href ?? i}>
              {group.heading && <MenuLink link={group.heading} pathname={pathname} />}
              {group.links.length > 0 && (
                <ul
                  className={
                    group.heading ? 'mt-4 space-y-2 border-l border-line pl-3' : 'space-y-4'
                  }
                >
                  {group.links.map((link) => (
                    <li key={link.href}>
                      <MenuLink link={link} pathname={pathname} minor={Boolean(group.heading)} />
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
        <Link
          href={menu.hub.href}
          className="mt-6 block border-t border-line pt-4 text-sm font-medium text-accent hover:underline"
        >
          {menu.hub.label} →
        </Link>
      </div>
    </div>
  )
}

/** One destination: a label, and a line of description when the link has one. */
function MenuLink({
  link,
  pathname,
  minor = false,
}: {
  link: NavLink
  pathname: string
  /** A child under a heading: smaller and quieter than a top-level entry. */
  minor?: boolean
}) {
  return (
    <Link
      href={link.href}
      aria-current={pathname === link.href ? 'page' : undefined}
      className="group block"
    >
      <span
        className={cn(
          'text-sm transition-colors group-hover:text-accent',
          minor ? 'text-fg-secondary' : 'font-medium text-fg'
        )}
      >
        {link.label}
      </span>
      {link.description && (
        <span className="mt-0.5 block text-sm leading-snug text-fg-muted">{link.description}</span>
      )}
    </Link>
  )
}
